import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Pagination, usePagination } from '../shared/Pagination';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Card,
  CardBody,
  CardHeader,
  useToast,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Image,
  Textarea,
  FormControl,
  FormLabel,
  Input,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Icon,
  IconButton,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Link,
  Checkbox,
} from '@chakra-ui/react';
import { FaFileContract, FaEye, FaEdit, FaCheck, FaTimes, FaSignature, FaUserMd, FaVial, FaUserNurse, FaDownload, FaCog } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import jsPDF from 'jspdf';
import SignatureCanvas from 'react-signature-canvas';

const AgreementManagement = () => {
  const companySigPad = useRef(null);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [labs, setLabs] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [phlebotomists, setPhlebotomists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSignedOnly, setShowSignedOnly] = useState(true);
  
  const [paginatedLabs, labPaginationProps] = usePagination(labs, 25);
  const [paginatedDoctors, doctorPaginationProps] = usePagination(doctors, 25);
  const [paginatedPhlebs, phlebPaginationProps] = usePagination(phlebotomists, 25);

  // Selected items for viewing
  const [selectedLab, setSelectedLab] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedPhlebotomist, setSelectedPhlebotomist] = useState(null);
  const [selectedAgreementType, setSelectedAgreementType] = useState(null); // 'lab', 'doctor', 'phlebotomist'
  
  // Templates for each agreement type
  const [labTemplate, setLabTemplate] = useState('');
  const [doctorTemplate, setDoctorTemplate] = useState('');
  const [phlebotomistTemplate, setPhlebotomistTemplate] = useState('');
  
  // Company signature
  const [companySignature, setCompanySignature] = useState('');
  const [companySignerName, setCompanySignerName] = useState('Authorized Representative');
  const [companySignerTitle, setCompanySignerTitle] = useState('Manager');
  
  // Modal controls
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isSignatureOpen, onOpen: onSignatureOpen, onClose: onSignatureClose } = useDisclosure();
  const [editingTemplateType, setEditingTemplateType] = useState('lab'); // 'lab', 'doctor', 'phlebotomist'
  
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');
  const agreementBgColor = useColorModeValue('gray.50', 'gray.900');
  const signatureBgColor = useColorModeValue('white', 'gray.800');

  // Default templates
  const defaultLabTemplate = `LABORATORY PARTNERSHIP AGREEMENT

This Agreement is entered into between At Home Blood Draw Inc ("Company") and {LAB_NAME} ("Laboratory").

1. SERVICES
Laboratory agrees to provide clinical laboratory testing services for samples collected through Company's mobile phlebotomy network.

2. PLACEMENT PRIORITY
Laboratory will receive {PLACEMENT_PRIORITY} placement priority in Company's selection algorithm based on the {PLAN_NAME} subscription plan.

3. BILLING
Laboratory agrees to pay a monthly fee of ${'{MONTHLY_FEE}'} for access to Company's platform and services, billed every 28 days.

4. QUALITY STANDARDS
Laboratory warrants that all testing will be performed in accordance with applicable regulatory standards including CLIA, CAP, and state regulations.

5. DATA SHARING
Laboratory agrees to provide test results in standardized formats and maintain patient confidentiality in accordance with HIPAA regulations.

6. TERM
This agreement shall remain in effect until terminated by either party with 30 days written notice.

By signing below, the authorized representative of Laboratory acknowledges reading, understanding, and agreeing to be bound by the terms of this Agreement.`;

  const defaultDoctorTemplate = `PHYSICIAN AUTHORIZATION AGREEMENT

This Agreement is entered into between At Home Blood Draw Inc ("Company") and {DOCTOR_NAME} ("Physician").

1. AUTHORIZATION
Physician authorizes Company and its network of mobile phlebotomists to collect blood samples from Physician's patients based on valid physician orders.

2. ORDER VERIFICATION
Physician agrees to provide valid orders and acknowledges that all samples will be collected and processed in accordance with applicable medical and laboratory standards.

3. PATIENT INFORMATION
Physician agrees to provide accurate patient information and authorizes Company to share necessary information with partner laboratories for testing purposes.

4. COMPLIANCE
All parties agree to comply with HIPAA, CLIA, and applicable state and federal regulations regarding patient privacy and sample handling.

5. LIABILITY
Physician acknowledges that Company acts as a service provider and that responsibility for patient care remains with the ordering physician.

By signing below, Physician acknowledges reading, understanding, and agreeing to be bound by the terms of this Agreement.`;

  const defaultPhlebotomistTemplate = `MOBILE PHLEBOTOMIST SERVICE AGREEMENT

This Agreement is entered into between At Home Blood Draw Inc ("Company") and {PHLEBOTOMIST_NAME} ("Phlebotomist").

1. SERVICES
Phlebotomist agrees to provide mobile phlebotomy services for Company's network, collecting blood samples from patients in accordance with physician orders.

2. QUALIFICATIONS
Phlebotomist warrants that they hold all necessary licenses, certifications, and training required to perform phlebotomy services in their service area.

3. SERVICE AREAS
Phlebotomist will operate within designated service areas as defined in their profile and may only accept appointments within these areas.

4. EQUIPMENT AND SUPPLIES
Phlebotomist is responsible for maintaining appropriate equipment and supplies necessary for safe blood collection procedures.

5. COMPLIANCE
Phlebotomist agrees to follow all Company protocols, maintain patient confidentiality (HIPAA compliance), and document all procedures accurately.

6. COMPENSATION
For each patient specimen collected, the Company shall pay Phlebotomist the sum of ${'{COMPENSATION_PER_DRAW}'} per draw. Phlebotomist shall submit an invoice to the Company each month detailing the specimens collected. The Company shall pay such invoice within 15 days of receipt. A $10 payment delay fee will apply if the invoice is not paid within 15 days.

7. PAYMENT TERMS
Payment will be made via the method agreed upon between the parties. All invoices must include the date of service, patient information (as permitted by HIPAA), and the number of draws performed.

By signing below, Phlebotomist acknowledges reading, understanding, and agreeing to be bound by the terms of this Agreement.`;

  useEffect(() => {
    fetchAllAgreements();
    loadTemplates();
  }, []);

  // Refetch when filter changes
  useEffect(() => {
    fetchAllAgreements();
  }, [showSignedOnly]);

  const fetchAllAgreements = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchLabs(),
        fetchDoctors(),
        fetchPhlebotomists(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLabs = async () => {
    try {
      let query = supabase
        .from('labs')
        .select(`
          id, name, partnership_status, subscription_plan, monthly_fee,
          agreement_signed, agreement_date, signer_name, signer_title, signature_data,
          enrollment_date, contact_email, state, city
        `);

      // If filtering by signed only, apply filter at database level and prioritize signed labs
      if (showSignedOnly) {
        query = query.eq('agreement_signed', true);
      }

      query = query.order('agreement_date', { ascending: false, nullsLast: true });

      // Increase limit to ensure we get all signed labs
      if (showSignedOnly) {
        query = query.limit(10000); // Get more rows when filtering
      } else {
        query = query.limit(1000); // Default limit
      }

      const { data, error } = await query;

      if (error) throw error;
      setLabs(data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch lab agreements',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchDoctors = async () => {
    try {
      let query = supabase
        .from('doctors')
        .select(`
          id, full_name, email, phone, clinic_name,
          agreement_signed, agreement_signed_at, agreement_signature_url,
          agreement_signed_name, agreement_signed_date, agreement_signed_email,
          agreement_signed_phone, agreement_signed_npi, agreement_signed_license,
          agreement_pdf_url
        `);

      // Apply filter at database level if signed only
      if (showSignedOnly) {
        query = query.eq('agreement_signed', true);
      }

      query = query.order('agreement_signed_at', { ascending: false, nullsLast: true });

      if (showSignedOnly) {
        query = query.limit(10000);
      } else {
        query = query.limit(1000);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching doctors:', error);
        throw error;
      }
      setDoctors(data || []);
    } catch (error) {
      console.error('Doctor fetch error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch doctor agreements',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchPhlebotomists = async () => {
    try {
      let query = supabase
        .from('phlebotomist_profiles')
        .select(`
          id, full_name, company_name, email, phone,
          agreement_signed, agreement_signed_at, agreement_date,
          agreement_signature, agreement_company_name, agreement_printed_name
        `);

      // Apply filter at database level if signed only
      if (showSignedOnly) {
        query = query.eq('agreement_signed', true);
      }

      query = query.order('agreement_signed_at', { ascending: false, nullsLast: true });

      if (showSignedOnly) {
        query = query.limit(10000);
      } else {
        query = query.limit(1000);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPhlebotomists(data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch phlebotomist agreements',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadTemplates = () => {
    const storedLabTemplate = localStorage.getItem('labAgreementTemplate');
    const storedDoctorTemplate = localStorage.getItem('doctorAgreementTemplate');
    const storedPhlebotomistTemplate = localStorage.getItem('phlebotomistAgreementTemplate');
    
    setLabTemplate(storedLabTemplate || defaultLabTemplate);
    setDoctorTemplate(storedDoctorTemplate || defaultDoctorTemplate);
    setPhlebotomistTemplate(storedPhlebotomistTemplate || defaultPhlebotomistTemplate);
    
    // Load company signature
    const storedSignature = localStorage.getItem('companySignature');
    const storedSignerName = localStorage.getItem('companySignerName');
    const storedSignerTitle = localStorage.getItem('companySignerTitle');
    
    if (storedSignature) setCompanySignature(storedSignature);
    if (storedSignerName) setCompanySignerName(storedSignerName);
    if (storedSignerTitle) setCompanySignerTitle(storedSignerTitle);
  };

  const saveTemplate = (type) => {
    const key = `${type}AgreementTemplate`;
    const template = type === 'lab' ? labTemplate : type === 'doctor' ? doctorTemplate : phlebotomistTemplate;
    localStorage.setItem(key, template);
    toast({
      title: 'Template Saved',
      description: `${type.charAt(0).toUpperCase() + type.slice(1)} agreement template has been updated`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    onEditClose();
  };

  const openEditTemplate = (type) => {
    setEditingTemplateType(type);
    onEditOpen();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCompanySignature(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      toast({
        title: 'Invalid File',
        description: 'Please upload an image file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSignatureDraw = () => {
    if (companySigPad.current && !companySigPad.current.isEmpty()) {
      const signatureData = companySigPad.current.getCanvas().toDataURL('image/png');
      setCompanySignature(signatureData);
    }
  };

  const clearSignature = () => {
    if (companySigPad.current) {
      companySigPad.current.clear();
    }
    setCompanySignature('');
  };

  const saveCompanySignature = () => {
    let signatureToSave = companySignature;
    
    // Get signature from canvas if available
    if (companySigPad.current && !companySigPad.current.isEmpty()) {
      signatureToSave = companySigPad.current.getCanvas().toDataURL('image/png');
      setCompanySignature(signatureToSave);
    }
    
    // Validate that we have a signature to save
    if (!signatureToSave || signatureToSave.trim() === '') {
      toast({
        title: 'Signature Required',
        description: 'Please draw or upload a signature before saving',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Save to localStorage using the actual signature data
    localStorage.setItem('companySignature', signatureToSave);
    localStorage.setItem('companySignerName', companySignerName);
    localStorage.setItem('companySignerTitle', companySignerTitle);
    
    toast({
      title: 'Company Signature Saved',
      description: 'The company signature will now be included in all PDFs',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    onSignatureClose();
  };

  const viewAgreement = (type, item) => {
    setSelectedAgreementType(type);
    if (type === 'lab') {
      setSelectedLab(item);
      setSelectedDoctor(null);
      setSelectedPhlebotomist(null);
    } else if (type === 'doctor') {
      setSelectedDoctor(item);
      setSelectedLab(null);
      setSelectedPhlebotomist(null);
    } else {
      setSelectedPhlebotomist(item);
      setSelectedLab(null);
      setSelectedDoctor(null);
    }
    onViewOpen();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
    return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (isSigned) => {
    if (isSigned) {
      return <Badge colorScheme="green">Signed</Badge>;
    }
    return <Badge colorScheme="gray">Not Signed</Badge>;
  };

  // Generate PDF with full agreement and both signatures
  const generateAgreementPDF = async (type, item) => {
    const doc = new jsPDF();
    
    // Set document properties
    doc.setProperties({
      title: `${type.charAt(0).toUpperCase() + type.slice(1)} Agreement`,
      subject: 'At Home Blood Draw Inc Partnership Agreement',
      author: 'At Home Blood Draw Inc',
      creator: 'Agreement Management System'
    });

    let yPos = 20;
    const pageWidth = 210; // A4 width in mm
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('At Home Blood Draw Inc', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    if (type === 'lab') {
      doc.text('Laboratory Partnership Agreement', pageWidth / 2, yPos, { align: 'center' });
    } else if (type === 'doctor') {
      doc.text('Physician Authorization Agreement', pageWidth / 2, yPos, { align: 'center' });
    } else {
      doc.text('Mobile Phlebotomist Service Agreement', pageWidth / 2, yPos, { align: 'center' });
    }
    yPos += 15;

    // Agreement Text
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const agreementText = await getFullAgreementText(type, item);
    const lines = doc.splitTextToSize(agreementText, contentWidth);
    
    lines.forEach((line) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, margin, yPos);
      yPos += 6;
    });

    // Add new page for signatures
    doc.addPage();
    yPos = 50;

    // Signatures Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURES', pageWidth / 2, yPos, { align: 'center' });
    yPos += 20;

    // Party 1 Signature (Lab/Doctor/Phlebotomist)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    if (type === 'lab') {
      doc.text('Laboratory:', margin, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(item.name || 'Laboratory', margin + 40, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 10;
      
      if (item.signer_name) {
        doc.text(`By: ${item.signer_name}`, margin, yPos);
        if (item.signer_title) {
          doc.text(`Title: ${item.signer_title}`, margin + 80, yPos);
        }
        yPos += 10;
      }
      
      if (item.signature_data) {
        try {
          doc.addImage(item.signature_data, 'PNG', margin, yPos, 60, 20);
          yPos += 25;
        } catch (error) {
          console.error('Error adding signature image:', error);
          doc.line(margin, yPos + 10, margin + 60, yPos + 10);
          yPos += 20;
        }
      } else {
        doc.line(margin, yPos + 10, margin + 60, yPos + 10);
        yPos += 20;
      }
      
      if (item.agreement_date) {
        doc.text(`Date: ${formatDate(item.agreement_date)}`, margin, yPos);
      }
    } else if (type === 'doctor') {
      doc.text('Physician:', margin, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(item.agreement_signed_name || item.full_name || 'Physician', margin + 40, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 10;
      
      if (item.agreement_signature_url) {
        try {
          doc.addImage(item.agreement_signature_url, 'PNG', margin, yPos, 60, 20);
          yPos += 25;
        } catch (error) {
          console.error('Error adding signature image:', error);
          doc.line(margin, yPos + 10, margin + 60, yPos + 10);
          yPos += 20;
        }
      } else {
        doc.line(margin, yPos + 10, margin + 60, yPos + 10);
        yPos += 20;
      }
      
      if (item.agreement_signed_at || item.agreement_signed_date) {
        doc.text(`Date: ${formatDate(item.agreement_signed_at || item.agreement_signed_date)}`, margin, yPos);
      }
    } else {
      doc.text('Phlebotomist:', margin, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(item.agreement_printed_name || item.full_name || 'Phlebotomist', margin + 40, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 10;
      
      if (item.agreement_signature) {
        try {
          doc.addImage(item.agreement_signature, 'PNG', margin, yPos, 60, 20);
          yPos += 25;
        } catch (error) {
          console.error('Error adding signature image:', error);
          doc.line(margin, yPos + 10, margin + 60, yPos + 10);
          yPos += 20;
        }
      } else {
        doc.line(margin, yPos + 10, margin + 60, yPos + 10);
        yPos += 20;
      }
      
      if (item.agreement_signed_at || item.agreement_date) {
        doc.text(`Date: ${formatDate(item.agreement_signed_at || item.agreement_date)}`, margin, yPos);
      }
    }

    yPos += 30;

    // Company Signature (At Home Blood Draw Inc)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('At Home Blood Draw Inc:', margin + 90, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text('At Home Blood Draw Inc', margin + 90, yPos + 10);
    yPos += 20;
    
    doc.setFont('helvetica', 'normal');
    doc.text(`By: ${companySignerName}`, margin + 90, yPos);
    yPos += 8;
    doc.text(`Name: ${companySignerName}`, margin + 90, yPos);
    yPos += 8;
    doc.text(`Title: ${companySignerTitle}`, margin + 90, yPos);
    yPos += 15;
    
    // Add company signature image if available
    if (companySignature) {
      try {
        doc.addImage(companySignature, 'PNG', margin + 90, yPos, 60, 20);
        yPos += 25;
      } catch (error) {
        console.error('Error adding company signature to PDF:', error);
        doc.line(margin + 90, yPos + 10, margin + 150, yPos + 10);
        yPos += 20;
      }
    } else {
      // Signature line for company
      doc.line(margin + 90, yPos, margin + 150, yPos);
      yPos += 20;
    }
    yPos += 8;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, margin + 90, yPos);

    // Generate filename
    const entityName = type === 'lab' ? item.name : type === 'doctor' ? (item.agreement_signed_name || item.full_name) : (item.agreement_printed_name || item.full_name);
    const filename = `Agreement-${entityName.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    
    doc.save(filename);
    
    toast({
      title: 'PDF Generated',
      description: `Agreement PDF has been downloaded`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Generate full agreement text with signature (synchronous version for display)
  const getFullAgreementTextSync = (type, item) => {
    if (!item || !type) return 'No agreement data available.';

    let template = '';
    let agreementText = '';

    try {
      if (type === 'lab') {
        template = labTemplate || defaultLabTemplate;
        const signedDate = item.agreement_date ? formatDate(item.agreement_date) : 'Not signed';
        agreementText = template
          .replace(/{LAB_NAME}/g, item.name || 'Laboratory')
          .replace(/{PLAN_NAME}/g, item.subscription_plan || 'Standard')
          .replace(/{PLACEMENT_PRIORITY}/g, item.subscription_plan === 'premium' ? 'Premium' : item.subscription_plan === 'standard' ? 'Standard' : item.subscription_plan === 'professional' ? 'Professional' : item.subscription_plan === 'enterprise' ? 'Enterprise' : item.subscription_plan === 'starter' ? 'Starter' : 'Basic')
          .replace(/{MONTHLY_FEE}/g, String(item.monthly_fee || '0'))
          .replace(/{LAB_STATE}/g, item.state || 'N/A')
          .replace(/{LAB_CITY}/g, item.city || 'N/A')
          .replace(/{SIGNED_DATE}/g, signedDate)
          .replace(/{AGREEMENT_DATE}/g, signedDate);
      } else if (type === 'doctor') {
        template = doctorTemplate || defaultDoctorTemplate;
        const signedDate = item.agreement_signed_at || item.agreement_signed_date ? formatDate(item.agreement_signed_at || item.agreement_signed_date) : 'Not signed';
        agreementText = template
          .replace(/{DOCTOR_NAME}/g, item.agreement_signed_name || item.full_name || 'Physician')
          .replace(/{CLINIC_NAME}/g, item.clinic_name || 'Clinic')
          .replace(/{NPI}/g, item.agreement_signed_npi || item.npi_number || 'N/A')
          .replace(/{SIGNED_DATE}/g, signedDate)
          .replace(/{AGREEMENT_DATE}/g, signedDate);
      } else if (type === 'phlebotomist') {
        template = phlebotomistTemplate || defaultPhlebotomistTemplate;
        const signedDate = item.agreement_signed_at || item.agreement_date ? formatDate(item.agreement_signed_at || item.agreement_date) : 'Not signed';
        const compensationAmount = item.lab_draw_fee ? `$${parseFloat(item.lab_draw_fee).toFixed(2)}` : (item.agreement_compensation_per_draw ? `$${parseFloat(item.agreement_compensation_per_draw).toFixed(2)}` : '$0.00');
        agreementText = template
          .replace(/{PHLEBOTOMIST_NAME}/g, item.agreement_printed_name || item.full_name || 'Phlebotomist')
          .replace(/{COMPANY_NAME}/g, item.agreement_company_name || item.company_name || 'Company')
          .replace(/{SERVICE_AREA}/g, 'Designated service areas')
          .replace(/{COMPENSATION_PER_DRAW}/g, compensationAmount)
          .replace(/{LAB_DRAW_FEE}/g, compensationAmount)
          .replace(/{SIGNED_DATE}/g, signedDate)
          .replace(/{AGREEMENT_DATE}/g, signedDate);
      }

      return agreementText || 'Agreement template not available.';
    } catch (error) {
      console.error('Error generating agreement text:', error);
      return 'Error loading agreement text.';
    }
  };

  // Generate full agreement text with signature (async version for PDF with history data)
  const getFullAgreementText = async (type, item) => {
    if (!item || !type) return 'No agreement data available.';

    let template = '';
    let agreementText = '';

    try {
      if (type === 'lab') {
        template = labTemplate || defaultLabTemplate;
        const signedDate = item.agreement_date ? formatDate(item.agreement_date) : 'Not signed';
        agreementText = template
          .replace(/{LAB_NAME}/g, item.name || 'Laboratory')
          .replace(/{PLAN_NAME}/g, item.subscription_plan || 'Standard')
          .replace(/{PLACEMENT_PRIORITY}/g, item.subscription_plan === 'premium' ? 'Premium' : item.subscription_plan === 'standard' ? 'Standard' : item.subscription_plan === 'professional' ? 'Professional' : item.subscription_plan === 'enterprise' ? 'Enterprise' : item.subscription_plan === 'starter' ? 'Starter' : 'Basic')
          .replace(/{MONTHLY_FEE}/g, String(item.monthly_fee || '0'))
          .replace(/{LAB_STATE}/g, item.state || 'N/A')
          .replace(/{LAB_CITY}/g, item.city || 'N/A')
          .replace(/{SIGNED_DATE}/g, signedDate)
          .replace(/{AGREEMENT_DATE}/g, signedDate);
      } else if (type === 'doctor') {
        template = doctorTemplate || defaultDoctorTemplate;
        const signedDate = item.agreement_signed_at || item.agreement_signed_date ? formatDate(item.agreement_signed_at || item.agreement_signed_date) : 'Not signed';
        agreementText = template
          .replace(/{DOCTOR_NAME}/g, item.agreement_signed_name || item.full_name || 'Physician')
          .replace(/{CLINIC_NAME}/g, item.clinic_name || 'Clinic')
          .replace(/{NPI}/g, item.agreement_signed_npi || item.npi_number || 'N/A')
          .replace(/{SIGNED_DATE}/g, signedDate)
          .replace(/{AGREEMENT_DATE}/g, signedDate);
      } else if (type === 'phlebotomist') {
        template = phlebotomistTemplate || defaultPhlebotomistTemplate;
        
        // Try to fetch current agreement from history if available
        let currentAgreement = null;
        let currentPaymentInfo = null;
        
        if (item?.current_agreement_id) {
          try {
            const { data: agreementData } = await supabase
              .from('phlebotomist_agreement_history')
              .select('*')
              .eq('id', item.current_agreement_id)
              .single();
            if (agreementData) currentAgreement = agreementData;
          } catch (error) {
            console.error('Error fetching agreement history:', error);
          }
        }
        
        if (item?.current_payment_info_id) {
          try {
            const { data: paymentData } = await supabase
              .from('phlebotomist_payment_info_history')
              .select('*')
              .eq('id', item.current_payment_info_id)
              .single();
            if (paymentData) currentPaymentInfo = paymentData;
          } catch (error) {
            console.error('Error fetching payment info history:', error);
          }
        }
        
        // Get per-draw fee from current agreement history, fallback to legacy field
        const perDrawFee = currentAgreement?.per_draw_fee !== null && currentAgreement?.per_draw_fee !== undefined
          ? `$${parseFloat(currentAgreement.per_draw_fee).toFixed(2)}`
          : (item.lab_draw_fee ? `$${parseFloat(item.lab_draw_fee).toFixed(2)}` : (item.agreement_compensation_per_draw ? `$${parseFloat(item.agreement_compensation_per_draw).toFixed(2)}` : '$____'));
        
        // Get payment frequency and method from current agreement
        const paymentFrequency = currentAgreement?.payment_frequency || 'weekly';
        const paymentMethod = currentAgreement?.payment_method || 'ACH';
        
        // Get bank info from current payment info
        const bankAccountName = currentPaymentInfo?.bank_account_name || '_________';
        const bankName = currentPaymentInfo?.bank_name || '_________';
        const bankRoutingNumber = currentPaymentInfo?.bank_routing_number || '_________';
        const bankAccountNumber = currentPaymentInfo?.bank_account_number 
          ? '***' + currentPaymentInfo.bank_account_number.slice(-4) 
          : '_________';
        
        const signedDate = item.agreement_signed_at || item.agreement_date ? formatDate(item.agreement_signed_at || item.agreement_date) : 'Not signed';
        const effectiveDate = currentAgreement?.agreement_effective_date
          ? formatDate(currentAgreement.agreement_effective_date)
          : signedDate;
        
        agreementText = template
          .replace(/{PER_DRAW_FEE}/g, perDrawFee)
          .replace(/{COMPENSATION_PER_DRAW}/g, perDrawFee) // Legacy placeholder support
          .replace(/{LAB_DRAW_FEE}/g, perDrawFee) // Legacy placeholder support
          .replace(/{PAYMENT_FREQUENCY}/g, paymentFrequency === 'weekly' ? 'weekly, one week in arrears' : paymentFrequency)
          .replace(/{PAYMENT_METHOD}/g, paymentMethod)
          .replace(/{BANK_ACCOUNT_NAME}/g, bankAccountName)
          .replace(/{BANK_NAME}/g, bankName)
          .replace(/{BANK_ROUTING_NUMBER}/g, bankRoutingNumber)
          .replace(/{BANK_ACCOUNT_NUMBER}/g, bankAccountNumber)
          .replace(/{EFFECTIVE_DATE}/g, effectiveDate)
          .replace(/{SIGNED_DATE}/g, signedDate)
          .replace(/{AGREEMENT_DATE}/g, signedDate) // Legacy placeholder support
          .replace(/{PRINTED_NAME}/g, item.agreement_printed_name || item.full_name || 'Phlebotomist')
          .replace(/{PHLEBOTOMIST_NAME}/g, item.agreement_printed_name || item.full_name || 'Phlebotomist') // Legacy placeholder support
          .replace(/{COMPANY_NAME}/g, item.agreement_company_name || item.company_name || 'Company')
          .replace(/{SERVICE_AREA}/g, 'Designated service areas'); // Legacy placeholder support
      }

      return agreementText || 'Agreement template not available.';
    } catch (error) {
      console.error('Error generating agreement text:', error);
      return 'Error loading agreement text.';
    }
  };

  // Calculate stats for all types
  const stats = {
    labs: {
    total: labs.length,
      signed: labs.filter(l => l.agreement_signed).length,
      unsigned: labs.filter(l => !l.agreement_signed).length,
    },
    doctors: {
      total: doctors.length,
      signed: doctors.filter(d => d.agreement_signed).length,
      unsigned: doctors.filter(d => !d.agreement_signed).length,
    },
    phlebotomists: {
      total: phlebotomists.length,
      signed: phlebotomists.filter(p => p.agreement_signed).length,
      unsigned: phlebotomists.filter(p => !p.agreement_signed).length,
    },
    overall: {
      total: labs.length + doctors.length + phlebotomists.length,
      signed: labs.filter(l => l.agreement_signed).length + 
              doctors.filter(d => d.agreement_signed).length + 
              phlebotomists.filter(p => p.agreement_signed).length,
    },
  };

  const getCurrentTemplate = () => {
    if (editingTemplateType === 'lab') return labTemplate;
    if (editingTemplateType === 'doctor') return doctorTemplate;
    return phlebotomistTemplate;
  };

  const setCurrentTemplate = (value) => {
    if (editingTemplateType === 'lab') setLabTemplate(value);
    else if (editingTemplateType === 'doctor') setDoctorTemplate(value);
    else setPhlebotomistTemplate(value);
  };

  const getTemplateLabel = () => {
    if (editingTemplateType === 'lab') return 'Lab';
    if (editingTemplateType === 'doctor') return 'Doctor';
    return 'Phlebotomist';
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <VStack align="start" spacing={2}>
            <Heading size="lg" color={headingColor}>
              Agreement Management
            </Heading>
            <Text color={textColor}>
              Manage all agreements: Lab partnerships, Doctor authorizations, and Phlebotomist service agreements
            </Text>
          </VStack>
        </HStack>

        {/* Overall Stats */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat>
            <StatLabel>Total Agreements</StatLabel>
            <StatNumber>{stats.overall.total}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Total Signed</StatLabel>
            <StatNumber color="green.500">{stats.overall.signed}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Total Unsigned</StatLabel>
            <StatNumber color="gray.500">{stats.overall.total - stats.overall.signed}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Completion Rate</StatLabel>
            <StatNumber color="blue.500">
              {stats.overall.total > 0 
                ? Math.round((stats.overall.signed / stats.overall.total) * 100) 
                : 0}%
            </StatNumber>
          </Stat>
        </SimpleGrid>

        {/* Tabs for Agreement Types */}
        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardHeader>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Heading size="md">Agreements by Type</Heading>
              <HStack spacing={2}>
                <Button size="sm" leftIcon={<FaEdit />} onClick={() => openEditTemplate('lab')}>
                  Edit Lab Template
                </Button>
                <Button size="sm" leftIcon={<FaEdit />} onClick={() => openEditTemplate('doctor')}>
                  Edit Doctor Template
                </Button>
                <Button size="sm" leftIcon={<FaEdit />} onClick={() => openEditTemplate('phlebotomist')}>
                  Edit Phlebotomist Template
                </Button>
                <Button size="sm" leftIcon={<FaCog />} onClick={onSignatureOpen} colorScheme="green">
                  Configure Company Signature
                </Button>
              </HStack>
              </HStack>
              <HStack>
                <Checkbox
                  isChecked={showSignedOnly}
                  onChange={(e) => setShowSignedOnly(e.target.checked)}
                  colorScheme="blue"
                >
                  Show signed agreements only
                </Checkbox>
              </HStack>
            </VStack>
          </CardHeader>
          <CardBody>
            <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
              <TabList>
                <Tab>
                  <Icon as={FaVial} mr={2} />
                  Lab Agreements ({stats.labs.signed}/{stats.labs.total})
                </Tab>
                <Tab>
                  <Icon as={FaUserMd} mr={2} />
                  Doctor Agreements ({stats.doctors.signed}/{stats.doctors.total})
                </Tab>
                <Tab>
                  <Icon as={FaUserNurse} mr={2} />
                  Phlebotomist Agreements ({stats.phlebotomists.signed}/{stats.phlebotomists.total})
                </Tab>
              </TabList>

              <TabPanels>
                {/* Lab Agreements Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                      <Stat>
                        <StatLabel>Total Labs</StatLabel>
                        <StatNumber>{stats.labs.total}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Signed</StatLabel>
                        <StatNumber color="green.500">{stats.labs.signed}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Unsigned</StatLabel>
                        <StatNumber color="gray.500">{stats.labs.unsigned}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Completion</StatLabel>
                        <StatNumber color="blue.500">
                          {stats.labs.total > 0 
                            ? Math.round((stats.labs.signed / stats.labs.total) * 100) 
                            : 0}%
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>

            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Lab Name</Th>
                    <Th>Status</Th>
                    <Th>Plan</Th>
                    <Th>Monthly Fee</Th>
                    <Th>Signed Date</Th>
                    <Th>Signer</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                          {labs.length === 0 ? (
                            <Tr>
                              <Td colSpan={7} textAlign="center">
                                <Text color={textColor}>
                                  {showSignedOnly ? 'No signed labs found' : 'No labs found'}
                                </Text>
                              </Td>
                            </Tr>
                          ) : (
                            paginatedLabs.map((lab) => (
                    <Tr key={lab.id}>
                      <Td>
                        <Text fontWeight="medium">{lab.name}</Text>
                        <Text fontSize="xs" color={textColor}>{lab.contact_email}</Text>
                      </Td>
                                <Td>{getStatusBadge(lab.agreement_signed)}</Td>
                      <Td>
                        {lab.subscription_plan ? (
                          <Badge size="sm" colorScheme="blue">
                            {lab.subscription_plan}
                          </Badge>
                        ) : '-'}
                      </Td>
                      <Td>${lab.monthly_fee || 0}/month</Td>
                      <Td>{formatDate(lab.agreement_date)}</Td>
                      <Td>
                        {lab.signer_name ? (
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">{lab.signer_name}</Text>
                            <Text fontSize="xs" color={textColor}>{lab.signer_title}</Text>
                          </VStack>
                        ) : '-'}
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <Tooltip label="View Agreement">
                            <IconButton
                              icon={<FaEye />}
                              size="sm"
                              variant="ghost"
                                        onClick={() => viewAgreement('lab', lab)}
                              isDisabled={!lab.agreement_signed}
                            />
                          </Tooltip>
                          {lab.signature_data && (
                            <Tooltip label="View Full Agreement">
                              <IconButton
                                icon={<FaSignature />}
                                size="sm"
                                variant="ghost"
                                colorScheme="green"
                                onClick={() => viewAgreement('lab', lab)}
                              />
                            </Tooltip>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                            ))
                          )}
                        </Tbody>
                      </Table>
                      <Pagination {...labPaginationProps} />
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Doctor Agreements Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                      <Stat>
                        <StatLabel>Total Doctors</StatLabel>
                        <StatNumber>{stats.doctors.total}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Signed</StatLabel>
                        <StatNumber color="green.500">{stats.doctors.signed}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Unsigned</StatLabel>
                        <StatNumber color="gray.500">{stats.doctors.unsigned}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Completion</StatLabel>
                        <StatNumber color="blue.500">
                          {stats.doctors.total > 0 
                            ? Math.round((stats.doctors.signed / stats.doctors.total) * 100) 
                            : 0}%
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>

                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Doctor Name</Th>
                            <Th>Clinic</Th>
                            <Th>Status</Th>
                            <Th>Signed Date</Th>
                            <Th>Contact</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {paginatedDoctors.length === 0 ? (
                            <Tr>
                              <Td colSpan={6} textAlign="center">
                                <Text color={textColor}>
                                  {showSignedOnly ? 'No signed doctors found' : 'No doctors found'}
                                </Text>
                              </Td>
                            </Tr>
                          ) : (
                            paginatedDoctors.map((doctor) => (
                              <Tr key={doctor.id}>
                                <Td>
                                  <Text fontWeight="medium">{doctor.full_name || 'N/A'}</Text>
                                  <Text fontSize="xs" color={textColor}>
                                    {doctor.agreement_signed_name && doctor.agreement_signed_name !== doctor.full_name
                                      ? `Signed as: ${doctor.agreement_signed_name}`
                                      : ''}
                                  </Text>
                                </Td>
                                <Td>{doctor.clinic_name || '-'}</Td>
                                <Td>{getStatusBadge(doctor.agreement_signed)}</Td>
                                <Td>{formatDate(doctor.agreement_signed_at || doctor.agreement_signed_date)}</Td>
                                <Td>
                                  <VStack align="start" spacing={0}>
                                    {doctor.email && <Text fontSize="xs">{doctor.email}</Text>}
                                    {doctor.phone && <Text fontSize="xs">{doctor.phone}</Text>}
                                  </VStack>
                                </Td>
                                <Td>
                                  <HStack spacing={1}>
                                    <Tooltip label="View Agreement">
                                      <IconButton
                                        icon={<FaEye />}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => viewAgreement('doctor', doctor)}
                                        isDisabled={!doctor.agreement_signed}
                                      />
                                    </Tooltip>
                                    {doctor.agreement_signature_url && (
                                      <Tooltip label="View Full Agreement">
                                        <IconButton
                                          icon={<FaSignature />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme="green"
                                          onClick={() => viewAgreement('doctor', doctor)}
                                        />
                                      </Tooltip>
                                    )}
                                    {doctor.agreement_pdf_url && (
                                      <Tooltip label="View PDF">
                                        <IconButton
                                          icon={<FaFileContract />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme="blue"
                                          as={Link}
                                          href={doctor.agreement_pdf_url}
                                          isExternal
                                        />
                                      </Tooltip>
                                    )}
                                  </HStack>
                                </Td>
                              </Tr>
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                    <Pagination {...doctorPaginationProps} />
                  </VStack>
                </TabPanel>

                {/* Phlebotomist Agreements Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                      <Stat>
                        <StatLabel>Total Phlebotomists</StatLabel>
                        <StatNumber>{stats.phlebotomists.total}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Signed</StatLabel>
                        <StatNumber color="green.500">{stats.phlebotomists.signed}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Unsigned</StatLabel>
                        <StatNumber color="gray.500">{stats.phlebotomists.unsigned}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Completion</StatLabel>
                        <StatNumber color="blue.500">
                          {stats.phlebotomists.total > 0 
                            ? Math.round((stats.phlebotomists.signed / stats.phlebotomists.total) * 100) 
                            : 0}%
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>

                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Phlebotomist Name</Th>
                            <Th>Company</Th>
                            <Th>Status</Th>
                            <Th>Signed Date</Th>
                            <Th>Contact</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {paginatedPhlebs.length === 0 ? (
                            <Tr>
                              <Td colSpan={6} textAlign="center">
                                <Text color={textColor}>
                                  {showSignedOnly ? 'No signed phlebotomists found' : 'No phlebotomists found'}
                                </Text>
                              </Td>
                            </Tr>
                          ) : (
                            paginatedPhlebs.map((phleb) => (
                              <Tr key={phleb.id}>
                                <Td>
                                  <Text fontWeight="medium">{phleb.full_name || 'N/A'}</Text>
                                  <Text fontSize="xs" color={textColor}>
                                    {phleb.agreement_printed_name && phleb.agreement_printed_name !== phleb.full_name
                                      ? `Signed as: ${phleb.agreement_printed_name}`
                                      : ''}
                                  </Text>
                                </Td>
                                <Td>{phleb.company_name || '-'}</Td>
                                <Td>{getStatusBadge(phleb.agreement_signed)}</Td>
                                <Td>{formatDate(phleb.agreement_signed_at || phleb.agreement_date)}</Td>
                                <Td>
                                  <VStack align="start" spacing={0}>
                                    {phleb.email && <Text fontSize="xs">{phleb.email}</Text>}
                                    {phleb.phone && <Text fontSize="xs">{phleb.phone}</Text>}
                                  </VStack>
                                </Td>
                                <Td>
                                  <HStack spacing={1}>
                                    <Tooltip label="View Agreement">
                                      <IconButton
                                        icon={<FaEye />}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => viewAgreement('phlebotomist', phleb)}
                                        isDisabled={!phleb.agreement_signed}
                                      />
                                    </Tooltip>
                                    {phleb.agreement_signature && (
                                      <Tooltip label="View Full Agreement">
                                        <IconButton
                                          icon={<FaSignature />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme="green"
                                          onClick={() => viewAgreement('phlebotomist', phleb)}
                                        />
                                      </Tooltip>
                                    )}
                                  </HStack>
                                </Td>
                              </Tr>
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                    <Pagination {...phlebPaginationProps} />
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>

      {/* View Agreement Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            Signed Agreement - {selectedLab?.name || selectedDoctor?.full_name || selectedPhlebotomist?.full_name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} maxH="80vh" overflowY="auto">
            {selectedLab && (
              <VStack spacing={6} align="stretch">
                {/* Agreement Text */}
                <Box>
                  <Text fontWeight="bold" fontSize="lg" mb={3}>Full Agreement:</Text>
                  <Box 
                    border="1px" 
                    borderColor={borderColor} 
                    p={6} 
                    borderRadius="md"
                    bg={agreementBgColor}
                  >
                    <Text 
                      whiteSpace="pre-wrap" 
                      fontSize="sm"
                      lineHeight="1.8"
                      color={textColor}
                      fontFamily="mono"
                    >
                      {selectedAgreementType && selectedLab ? getFullAgreementTextSync(selectedAgreementType, selectedLab) : 'Loading agreement...'}
                    </Text>
                  </Box>
                </Box>

                {/* Signature Section */}
                <Box>
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="bold" fontSize="lg">Signature:</Text>
                    <Button
                      leftIcon={<FaDownload />}
                      colorScheme="blue"
                      size="sm"
                      onClick={() => generateAgreementPDF(selectedAgreementType, selectedLab)}
                    >
                      Download PDF
                    </Button>
                  </HStack>
                  <Box 
                    border="1px" 
                    borderColor={borderColor} 
                    p={6} 
                    borderRadius="md"
                    bg={signatureBgColor}
                  >
                    <VStack spacing={4} align="stretch">
                      <SimpleGrid columns={2} spacing={4}>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Signer Name:</Text>
                          <Text fontSize="sm">{selectedLab.signer_name || '-'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Title:</Text>
                          <Text fontSize="sm">{selectedLab.signer_title || '-'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Signed Date:</Text>
                          <Text fontSize="sm">{formatDate(selectedLab.agreement_date)}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Plan:</Text>
                          <Text fontSize="sm">{selectedLab.subscription_plan || '-'} (${selectedLab.monthly_fee || 0}/month)</Text>
                        </Box>
                      </SimpleGrid>
                      {selectedLab.signature_data && (
                        <Box mt={4}>
                          <Text fontWeight="bold" fontSize="sm" mb={2}>Digital Signature:</Text>
                          <Box border="1px" borderColor={borderColor} p={4} borderRadius="md" bg="white">
                            <Image
                              src={selectedLab.signature_data}
                              alt="Digital Signature"
                              maxH="100px"
                              mx="auto"
                            />
                          </Box>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                </Box>
              </VStack>
            )}

            {selectedDoctor && (
              <VStack spacing={6} align="stretch">
                {/* Agreement Text */}
                <Box>
                  <Text fontWeight="bold" fontSize="lg" mb={3}>Full Agreement:</Text>
                  <Box 
                    border="1px" 
                    borderColor={borderColor} 
                    p={6} 
                    borderRadius="md"
                    bg={agreementBgColor}
                  >
                    <Text 
                      whiteSpace="pre-wrap" 
                      fontSize="sm"
                      lineHeight="1.8"
                      color={textColor}
                    >
                      {getFullAgreementTextSync(selectedAgreementType, selectedDoctor)}
                    </Text>
                  </Box>
                </Box>

                {/* Signature Section */}
                <Box>
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="bold" fontSize="lg">Signature:</Text>
                    <Button
                      leftIcon={<FaDownload />}
                      colorScheme="blue"
                      size="sm"
                      onClick={() => generateAgreementPDF(selectedAgreementType, selectedDoctor)}
                    >
                      Download PDF
                    </Button>
                  </HStack>
                  <Box 
                    border="1px" 
                    borderColor={borderColor} 
                    p={6} 
                    borderRadius="md"
                    bg={signatureBgColor}
                  >
                    <VStack spacing={4} align="stretch">
                      <SimpleGrid columns={2} spacing={4}>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Signed Name:</Text>
                          <Text fontSize="sm">{selectedDoctor.agreement_signed_name || selectedDoctor.full_name || '-'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Email:</Text>
                          <Text fontSize="sm">{selectedDoctor.agreement_signed_email || selectedDoctor.email || '-'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Phone:</Text>
                          <Text fontSize="sm">{selectedDoctor.agreement_signed_phone || selectedDoctor.phone || '-'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Signed Date:</Text>
                          <Text fontSize="sm">{formatDate(selectedDoctor.agreement_signed_at || selectedDoctor.agreement_signed_date)}</Text>
                        </Box>
                        {selectedDoctor.agreement_signed_npi && (
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">NPI Number:</Text>
                            <Text fontSize="sm">{selectedDoctor.agreement_signed_npi}</Text>
                          </Box>
                        )}
                        {selectedDoctor.agreement_signed_license && (
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">License Number:</Text>
                            <Text fontSize="sm">{selectedDoctor.agreement_signed_license}</Text>
                          </Box>
                        )}
                      </SimpleGrid>
                      {selectedDoctor.agreement_signature_url && (
                        <Box mt={4}>
                          <Text fontWeight="bold" fontSize="sm" mb={2}>Digital Signature:</Text>
                          <Box border="1px" borderColor={borderColor} p={4} borderRadius="md" bg="white">
                            <Image
                              src={selectedDoctor.agreement_signature_url}
                              alt="Digital Signature"
                              maxH="100px"
                              mx="auto"
                            />
                          </Box>
                        </Box>
                      )}
                      {selectedDoctor.agreement_pdf_url && (
                        <Box mt={4}>
                          <Link href={selectedDoctor.agreement_pdf_url} isExternal>
                            <Button leftIcon={<FaFileContract />} size="sm" variant="outline" colorScheme="blue">
                              View Signed PDF
                            </Button>
                          </Link>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                </Box>
              </VStack>
            )}

            {selectedPhlebotomist && (
              <VStack spacing={6} align="stretch">
                {/* Agreement Text */}
                <Box>
                  <Text fontWeight="bold" fontSize="lg" mb={3}>Full Agreement:</Text>
                  <Box 
                    border="1px" 
                    borderColor={borderColor} 
                    p={6} 
                    borderRadius="md"
                    bg={agreementBgColor}
                  >
                    <Text 
                      whiteSpace="pre-wrap" 
                      fontSize="sm"
                      lineHeight="1.8"
                      color={textColor}
                    >
                      {getFullAgreementTextSync(selectedAgreementType, selectedPhlebotomist)}
                    </Text>
                  </Box>
                </Box>

                {/* Signature Section */}
                <Box>
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="bold" fontSize="lg">Signature:</Text>
                    <Button
                      leftIcon={<FaDownload />}
                      colorScheme="blue"
                      size="sm"
                      onClick={() => generateAgreementPDF(selectedAgreementType, selectedPhlebotomist)}
                    >
                      Download PDF
                    </Button>
                  </HStack>
                  <Box 
                    border="1px" 
                    borderColor={borderColor} 
                    p={6} 
                    borderRadius="md"
                    bg={signatureBgColor}
                  >
                    <VStack spacing={4} align="stretch">
                      <SimpleGrid columns={2} spacing={4}>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Signed Name:</Text>
                          <Text fontSize="sm">{selectedPhlebotomist.agreement_printed_name || selectedPhlebotomist.full_name || '-'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Company Name:</Text>
                          <Text fontSize="sm">{selectedPhlebotomist.agreement_company_name || selectedPhlebotomist.company_name || '-'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Signed Date:</Text>
                          <Text fontSize="sm">{formatDate(selectedPhlebotomist.agreement_signed_at || selectedPhlebotomist.agreement_date)}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm">Email:</Text>
                          <Text fontSize="sm">{selectedPhlebotomist.email || '-'}</Text>
                        </Box>
                      </SimpleGrid>
                      {selectedPhlebotomist.agreement_signature && (
                        <Box mt={4}>
                          <Text fontWeight="bold" fontSize="sm" mb={2}>Digital Signature:</Text>
                          <Box border="1px" borderColor={borderColor} p={4} borderRadius="md" bg="white">
                            <Image
                              src={selectedPhlebotomist.agreement_signature}
                              alt="Digital Signature"
                              maxH="100px"
                              mx="auto"
                            />
                          </Box>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Company Signature Configuration Modal */}
      <Modal isOpen={isSignatureOpen} onClose={onSignatureClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Configure At Home Blood Draw Inc Signature</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Signer Name</FormLabel>
                <Input
                  value={companySignerName}
                  onChange={(e) => setCompanySignerName(e.target.value)}
                  placeholder="Authorized Representative"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Signer Title</FormLabel>
                <Input
                  value={companySignerTitle}
                  onChange={(e) => setCompanySignerTitle(e.target.value)}
                  placeholder="Manager"
                />
              </FormControl>

              <Divider />

              <Box>
                <FormLabel mb={3}>Company Signature</FormLabel>
                <Text fontSize="sm" color={textColor} mb={3}>
                  You can either draw a signature below or upload an image file
                </Text>
                
                {/* Signature Canvas */}
                <Box border="2px" borderColor={borderColor} borderRadius="md" p={4} mb={4}>
                  <SignatureCanvas
                    ref={companySigPad}
                    canvasProps={{
                      width: 500,
                      height: 200,
                      className: 'signature-canvas',
                    }}
                    backgroundColor="white"
                    onEnd={handleSignatureDraw}
                  />
                  <HStack mt={3} spacing={2}>
                    <Button size="sm" onClick={clearSignature} variant="outline">
                      Clear
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={handleSignatureDraw}
                      colorScheme="blue"
                      isDisabled={!companySigPad.current || companySigPad.current.isEmpty()}
                    >
                      Use Drawn Signature
                    </Button>
                  </HStack>
                </Box>

                {/* Image Upload */}
                <VStack spacing={3} align="stretch">
                  <Text fontSize="sm" fontWeight="bold">OR Upload Signature Image:</Text>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    display="none"
                  />
                  <Button
                    leftIcon={<FaEdit />}
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                  >
                    Upload Signature Image
                  </Button>
                </VStack>

                {/* Preview */}
                {companySignature && (
                  <Box mt={4}>
                    <Text fontWeight="bold" mb={2}>Signature Preview:</Text>
                    <Box border="1px" borderColor={borderColor} p={4} borderRadius="md" bg="white">
                      <Image
                        src={companySignature}
                        alt="Company Signature Preview"
                        maxH="100px"
                        mx="auto"
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              <HStack spacing={4} w="full" pt={4}>
                <Button onClick={onSignatureClose} variant="outline" flex={1}>
                  Cancel
                </Button>
                <Button onClick={saveCompanySignature} colorScheme="green" flex={1}>
                  Save Company Signature
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Edit Template Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit {getTemplateLabel()} Agreement Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Text fontSize="sm" color={textColor}>
                {editingTemplateType === 'lab' && (
                  <>Use placeholders: {'{LAB_NAME}'}, {'{PLAN_NAME}'}, {'{PLACEMENT_PRIORITY}'}, {'{MONTHLY_FEE}'}, {'{LAB_STATE}'}, {'{LAB_CITY}'}, {'{SIGNED_DATE}'}, {'{AGREEMENT_DATE}'}</>
                )}
                {editingTemplateType === 'doctor' && (
                  <>Use placeholders: {'{DOCTOR_NAME}'}, {'{CLINIC_NAME}'}, {'{NPI}'}, {'{SIGNED_DATE}'}, {'{AGREEMENT_DATE}'}</>
                )}
                {editingTemplateType === 'phlebotomist' && (
                  <>Use placeholders: {'{PHLEBOTOMIST_NAME}'}, {'{COMPANY_NAME}'}, {'{SERVICE_AREA}'}, {'{COMPENSATION_PER_DRAW}'} or {'{LAB_DRAW_FEE}'}, {'{SIGNED_DATE}'}, {'{AGREEMENT_DATE}'}</>
                )}
              </Text>
              <FormControl>
                <FormLabel>Agreement Template</FormLabel>
                <Textarea
                  value={getCurrentTemplate()}
                  onChange={(e) => setCurrentTemplate(e.target.value)}
                  rows={20}
                  fontFamily="mono"
                  fontSize="sm"
                />
              </FormControl>
              <HStack spacing={4} w="full">
                <Button onClick={onEditClose} variant="outline" flex={1}>
                  Cancel
                </Button>
                <Button onClick={() => saveTemplate(editingTemplateType)} colorScheme="blue" flex={1}>
                  Save Template
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AgreementManagement;
